import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence, useInView, useScroll, useTransform, animate } from 'framer-motion';
import { HardHat, Truck, Clock, Phone, DollarSign, Calculator, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SMSOrderCTA } from '../components/SMSOrderCTA';
const benefits = [{
  icon: DollarSign,
  title: 'Bulk Pricing',
  description: 'Volume discounts on all materials and dedicated account pricing for our regular partners.'
}, {
  icon: Clock,
  title: 'Priority Scheduling',
  description: 'Same-day and next-day delivery slots reserved specifically for contractors to keep jobs moving.'
}, {
  icon: Phone,
  title: 'Dedicated Support',
  description: 'Direct line to our dispatch team. No waiting in queue when you need materials fast.'
}, {
  icon: CheckCircle2,
  title: 'Flexible Billing',
  description: 'Net-30 terms available for qualified contractors to help manage cash flow.'
}, {
  icon: Truck,
  title: 'Job Site Delivery',
  description: 'Professional drivers who know how to navigate tight job sites and drop exactly where needed.'
}, {
  icon: Calculator,
  title: 'Material Calculator',
  description: 'Free project estimation and material planning assistance for complex jobs.'
}];
const steps = [{
  number: '01',
  title: 'Apply',
  description: 'Fill out the quick application form below with your company details and estimated volume.'
}, {
  number: '02',
  title: 'Get Approved',
  description: 'Our team will review your application and set up your account within 24 hours.'
}, {
  number: '03',
  title: 'Start Ordering',
  description: 'Access your discounted pricing and priority scheduling immediately upon approval.'
}];
const discountTiers = [{
  volume: '24-50 yards',
  discount: '5% Off',
  targetValue: 5,
  desc: 'Mid-size project discount'
}, {
  volume: '50-100 yards',
  discount: '10% Off',
  targetValue: 10,
  desc: 'Large project discount'
}, {
  volume: '100+ yards',
  discount: 'Call Us',
  targetValue: null,
  desc: 'Custom bulk pricing'
}];
function RingTrace({
  elementSize,
  delay,
  duration,
  isInView,
  startAngle
}: {
  elementSize: number;
  delay: number;
  duration: number;
  isInView: boolean;
  startAngle: number;
}) {
  const svgSize = elementSize + 4;
  const radius = elementSize / 2 + 1;
  const circumference = 2 * Math.PI * radius;
  return <svg width={svgSize} height={svgSize} className="absolute pointer-events-none z-20" style={{
    top: '50%',
    left: '50%',
    transform: `translate(-50%, -50%) rotate(${startAngle}deg)`
  }} data-id="element-3000">
      <motion.circle cx={svgSize / 2} cy={svgSize / 2} r={radius} fill="none" stroke="rgba(251, 191, 36, 0.3)" strokeWidth={2} strokeLinecap="round" initial={{
      strokeDasharray: circumference,
      strokeDashoffset: circumference
    }} animate={isInView ? {
      strokeDashoffset: 0
    } : {
      strokeDashoffset: circumference
    }} transition={{
      duration,
      ease: [0.25, 0.1, 0.25, 1],
      delay
    }} data-id="element-3001" />
    </svg>;
}
function AnimatedNumber({
  value
}: {
  value: number;
}) {
  const nodeRef = useRef<HTMLSpanElement>(null);
  const inView = useInView(nodeRef, {
    once: true,
    margin: '-50px'
  });
  useEffect(() => {
    if (inView && nodeRef.current) {
      const controls = animate(0, value, {
        duration: 1.2,
        ease: 'easeOut',
        onUpdate(v) {
          if (nodeRef.current) {
            nodeRef.current.textContent = Math.round(v).toString();
          }
        }
      });
      return () => controls.stop();
    }
  }, [inView, value]);
  return <span ref={nodeRef} data-id="element-3002">0</span>;
}
export function Subcontractors() {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    phone: '',
    email: '',
    licenseNumber: '',
    estimatedVolume: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const stepsRef = useRef<HTMLDivElement>(null);
  const stepsInView = useInView(stepsRef, {
    once: true,
    margin: '-100px'
  });
  const {
    scrollY
  } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, 150]);
  const ringParams = useMemo(() => ({
    ringDelays: [0.5, 2.0, 0.8],
    ringDurations: [8, 10, 7],
    ringAngles: [45, 220, 310],
    mobileRingAngles: [120, 300, 30],
    lineDelays: [0, 1.5],
    lineDurations: [9, 11]
  }), []);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitted(true);
    setIsSubmitting(false);
    setFormData({
      companyName: '',
      contactName: '',
      phone: '',
      email: '',
      licenseNumber: '',
      estimatedVolume: ''
    });
  };
  const inputClass = 'w-full bg-[#fdfcfb] dark:bg-[#3a3938] border border-stone-300 dark:border-[#525252] rounded-none px-4 py-3 text-sm text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-[#fbbf24]/50 focus:border-[#fbbf24]/50 focus:shadow-[0_0_0_4px_rgba(251,191,36,0.2)] outline-none transition-all';
  return <main className="min-h-screen bg-[#fdfbf7] dark:bg-[#2a2a2a] transition-colors duration-300 pb-20 relative overflow-hidden" data-id="element-3003">
      {/* Ambient Background Blobs (Dark Mode primarily) */}
      <div className="fixed inset-0 pointer-events-none z-0 hidden dark:block overflow-hidden" data-id="element-3004">
        <motion.div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#fbbf24] rounded-full mix-blend-screen opacity-[0.04] blur-[100px]" animate={{
        x: [0, 100, -50, 0],
        y: [0, -100, 50, 0]
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-3005" />
        <motion.div className="absolute bottom-1/4 right-1/4 w-[30rem] h-[30rem] bg-[#fbbf24] rounded-full mix-blend-screen opacity-[0.05] blur-[120px]" animate={{
        x: [0, -150, 100, 0],
        y: [0, 100, -100, 0]
      }} transition={{
        duration: 25,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-3006" />
      </div>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden bg-[#2c2c2c]" data-id="element-3007">
        <div className="absolute inset-0" data-id="element-3008">
          <motion.img src="https://cdn.magicpatterns.com/uploads/tn9AHnmsKjEsy7nQqUqaij/file_000000005a5471f5abe1156df7570912~2.png" alt="Concrete pour at foundation jobsite with crew" className="w-full h-full object-cover opacity-60 origin-center" animate={{
          scale: [1, 1.08, 1]
        }} transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-3009" />
          <div className="absolute inset-0 bg-black/30" data-id="element-3010" />
        </div>
        <motion.div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" style={{
        y: heroY
      }} data-id="element-3011">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="flex flex-col items-center justify-center mb-8" data-id="element-3012">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-black/40 backdrop-blur-md border border-white/10 text-white/90 text-xs font-bold tracking-widest uppercase mb-6 shadow-lg" data-id="element-3013">
              <motion.span className="w-2 h-2 rounded-full bg-[#fbbf24]" animate={{
              opacity: [1, 0.4, 1]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-3014" />
              Now Serving Pro Contractors
            </div>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#fbbf24]/10 backdrop-blur-sm border border-[#fbbf24]/20 shadow-[0_0_30px_rgba(251,191,36,0.2)]" data-id="element-3015">
              <HardHat className="w-8 h-8 text-[#fbbf24]" data-id="element-3016" />
            </div>
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="font-heading text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight" data-id="element-3017">
            Contractor <span className="text-[#fbbf24]" data-id="element-3018">Program</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-xl text-stone-200 max-w-2xl mx-auto font-medium font-sub leading-relaxed" data-id="element-3019">
            Partner with 253Gravel for bulk pricing, priority scheduling, and
            dedicated support to keep your projects running smoothly and
            profitably.
          </motion.p>
        </motion.div>
      </section>

      {/* SMS Order CTA — text-first ordering for contractors */}
      <SMSOrderCTA data-id="element-3020" />

      {/* Volume Discount Tiers */}
      <section className="py-20 bg-[#FAF6ED] dark:bg-[#474443] relative z-10" data-id="element-3021">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3022">
          <div className="text-center mb-12" data-id="element-3023">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 dark:text-white mb-4" data-id="element-3024">
              Volume Discount Tiers
            </h2>
            <p className="text-stone-600 dark:text-stone-300 text-lg" data-id="element-3025">
              Discounts applied automatically on qualifying orders.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto" data-id="element-3026">
            {discountTiers.map((tier, index) => <motion.div key={tier.volume} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} whileHover={{
            y: -4
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="group relative bg-white dark:bg-[#525050] rounded-xl p-6 border border-stone-200 dark:border-[#5e5b5b] text-center hover:border-[#fbbf24]/50 hover:shadow-[0_8px_30px_rgba(251,191,36,0.2)] transition-all duration-300 overflow-hidden" data-id="element-3027">
                {/* Gradient Sheen */}
                <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 dark:via-white/5 to-transparent -skew-x-12 translate-x-[-150%]" variants={{
              hover: {
                translateX: '150%',
                transition: {
                  duration: 0.7,
                  ease: 'easeInOut'
                }
              }
            }} data-id="element-3028" />

                <div className="text-stone-700 dark:text-stone-200 font-bold mb-2 relative z-10" data-id="element-3029">
                  {tier.volume}
                </div>
                <div className="text-2xl font-heading font-extrabold text-[#f59e0b] dark:text-[#fbbf24] mb-2 relative z-10" data-id="element-3030">
                  {tier.targetValue !== null ? <>
                      <AnimatedNumber value={tier.targetValue} data-id="element-3031" />% Off
                    </> : tier.discount}
                </div>
                <div className="text-sm text-stone-600 dark:text-stone-200 relative z-10" data-id="element-3032">
                  {tier.desc}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-[#FAF6ED] dark:bg-[#474443] relative z-10" data-id="element-3033">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3034">
          <div className="text-center mb-16" data-id="element-3035">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 dark:text-white mb-4" data-id="element-3036">
              Program Benefits
            </h2>
            <p className="text-stone-600 dark:text-stone-300 text-lg" data-id="element-3037">
              Everything you need to scale your business.
            </p>
          </div>
          <div className="space-y-16" data-id="element-3038">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-3039">
              {benefits.map((benefit, index) => <motion.div key={benefit.title} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} whileHover={{
              y: -4
            }} viewport={{
              once: true
            }} transition={{
              type: 'spring',
              stiffness: 80,
              delay: index * 0.1
            }} className="group relative bg-white dark:bg-[#525050] rounded-2xl p-8 border border-stone-200 dark:border-[#5e5b5b] hover:border-[#fbbf24]/40 hover:shadow-[inset_0_2px_10px_rgba(251,191,36,0.05),0_10px_30px_rgba(0,0,0,0.05)] dark:hover:shadow-[inset_0_2px_10px_rgba(251,191,36,0.1),0_10px_30px_rgba(0,0,0,0.25)] transition-all duration-300 overflow-hidden" data-id="element-3040">
                  <motion.div variants={{
                hover: {
                  scale: 1.1,
                  rotate: 4
                }
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 15
              }} className="inline-block mb-6 relative" data-id="element-3041">
                    <benefit.icon className="w-10 h-10 text-[#f59e0b] dark:text-[#fbbf24] relative z-10" data-id="element-3042" />
                    {/* Hairline accent under icon */}
                    <motion.div className="absolute -bottom-2 left-0 right-0 h-[2px] bg-[#fbbf24]/60 origin-left" variants={{
                  initial: {
                    scaleX: 0
                  },
                  hover: {
                    scaleX: 1
                  }
                }} initial="initial" data-id="element-3043" />
                  </motion.div>
                  <h3 className="font-heading text-xl font-bold text-stone-900 dark:text-white mb-3" data-id="element-3044">
                    {benefit.title}
                  </h3>
                  <p className="text-stone-600 dark:text-stone-200 leading-relaxed" data-id="element-3045">
                    {benefit.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white dark:bg-[#2a2a2a] relative z-10" data-id="element-3046">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3047">
          <div className="text-center mb-16" data-id="element-3048">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 dark:text-white mb-4" data-id="element-3049">
              How to Join
            </h2>
            <p className="text-stone-600 dark:text-stone-300 text-lg" data-id="element-3050">
              A simple process to get you started.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 relative" ref={stepsRef} data-id="element-3051">
            {/* Desktop Connecting Lines */}
            <div className="hidden md:block absolute top-12 left-[10%] right-[10%] h-[2px] bg-stone-200 dark:bg-[#525252]" data-id="element-3052">
              <motion.div initial={{
              width: '0%'
            }} animate={stepsInView ? {
              width: '50%'
            } : {
              width: '0%'
            }} transition={{
              duration: ringParams.lineDurations[0],
              ease: [0.25, 0.1, 0.25, 1],
              delay: ringParams.lineDelays[0]
            }} className="absolute inset-y-0 left-0 rounded-full" style={{
              backgroundColor: 'rgba(251, 191, 36, 0.6)',
              boxShadow: '0 0 4px rgba(251, 191, 36, 0.25)'
            }} data-id="element-3053" />
              <motion.div initial={{
              width: '0%'
            }} animate={stepsInView ? {
              width: '50%'
            } : {
              width: '0%'
            }} transition={{
              duration: ringParams.lineDurations[1],
              ease: [0.25, 0.1, 0.25, 1],
              delay: ringParams.lineDelays[1]
            }} className="absolute inset-y-0 left-[50%] rounded-full" style={{
              backgroundColor: 'rgba(251, 191, 36, 0.6)',
              boxShadow: '0 0 4px rgba(251, 191, 36, 0.25)'
            }} data-id="element-3054" />
            </div>

            {/* Mobile Connecting Lines */}
            <div className="md:hidden absolute top-0 bottom-0 left-8 w-[2px] bg-stone-200 dark:bg-[#525252]" data-id="element-3055">
              <motion.div initial={{
              height: '0%'
            }} animate={stepsInView ? {
              height: '50%'
            } : {
              height: '0%'
            }} transition={{
              duration: ringParams.lineDurations[0],
              ease: [0.25, 0.1, 0.25, 1],
              delay: ringParams.lineDelays[0]
            }} className="absolute inset-x-0 top-0 rounded-full" style={{
              backgroundColor: 'rgba(251, 191, 36, 0.6)',
              boxShadow: '0 0 4px rgba(251, 191, 36, 0.25)'
            }} data-id="element-3056" />
              <motion.div initial={{
              height: '0%'
            }} animate={stepsInView ? {
              height: '50%'
            } : {
              height: '0%'
            }} transition={{
              duration: ringParams.lineDurations[1],
              ease: [0.25, 0.1, 0.25, 1],
              delay: ringParams.lineDelays[1]
            }} className="absolute inset-x-0 top-[50%] rounded-full" style={{
              backgroundColor: 'rgba(251, 191, 36, 0.6)',
              boxShadow: '0 0 4px rgba(251, 191, 36, 0.25)'
            }} data-id="element-3057" />
            </div>

            {steps.map((step, index) => <motion.div key={step.number} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2,
            type: 'spring',
            stiffness: 100
          }} className="relative flex flex-col md:items-center pl-20 md:pl-0 h-full group" data-id="element-3058">
                {/* Step Number Node */}
                <motion.div animate={{
              scale: [1, 1.04, 1]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: index * 0.5
            }} className="absolute left-0 md:relative md:left-auto w-16 h-16 md:w-24 md:h-24 bg-stone-50 dark:bg-[#454545] border-4 border-white dark:border-[#2a2a2a] rounded-full flex items-center justify-center mb-8 shadow-[0_0_0_2px_rgba(251,191,36,0.25)] z-10 shrink-0" data-id="element-3059">
                  <span className="font-heading text-2xl md:text-3xl font-extrabold text-[#f59e0b] dark:text-[#fbbf24]" data-id="element-3060">
                    {step.number}
                  </span>
                  {/* Ring trace - desktop */}
                  <div className="hidden md:block" data-id="element-3061">
                    <RingTrace elementSize={96} delay={ringParams.ringDelays[index]} duration={ringParams.ringDurations[index]} isInView={stepsInView} startAngle={ringParams.ringAngles[index]} data-id="element-3062" />
                  </div>
                  {/* Ring trace - mobile */}
                  <div className="md:hidden" data-id="element-3063">
                    <RingTrace elementSize={64} delay={ringParams.ringDelays[index]} duration={ringParams.ringDurations[index]} isInView={stepsInView} startAngle={ringParams.mobileRingAngles[index]} data-id="element-3064" />
                  </div>
                </motion.div>

                {/* Content Card */}
                <div className="bg-white dark:bg-gradient-to-b dark:from-[#5a5a5a] dark:to-[#4a4a4a] rounded-2xl p-8 w-full md:text-center border border-stone-200 dark:border-[#666666]/60 dark:border-b-[#404040]/60 hover:border-[#fbbf24]/40 dark:hover:border-[#fbbf24]/40 transition-colors duration-300 relative overflow-hidden flex-1 flex flex-col shadow-sm dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_2px_8px_rgba(0,0,0,0.25)]" data-id="element-3065">
                  <span className="absolute -bottom-6 -right-4 font-heading text-8xl font-black text-stone-100 dark:text-[#5a5a5a]/40 group-hover:text-[#fbbf24]/15 group-hover:rotate-6 transition-all duration-500 pointer-events-none select-none origin-bottom-right" data-id="element-3066">
                    {step.number}
                  </span>
                  <h3 className="font-heading text-2xl font-bold text-stone-900 dark:text-white mb-4 relative z-10" data-id="element-3067">
                    {step.title}
                  </h3>
                  <p className="text-stone-800 dark:text-stone-200 leading-relaxed text-[15px] relative z-10" data-id="element-3068">
                    {step.description}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-24 bg-[#f0ece6] dark:bg-[#252525] relative z-10" data-id="element-3069">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3070">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-3071">
            {/* Image Column */}
            <div className="hidden lg:block relative" data-id="element-3072">
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} className="sticky top-32 aspect-[3/4] rounded-2xl overflow-hidden border border-stone-400 dark:border-[#454443]" data-id="element-3073">
                <img src="https://cdn.magicpatterns.com/uploads/5XaxepFncit8gaVhvc86Zp/253_gravel_tacoma_premier_gravel_supplier.png" alt="253 Gravel Premier Supplier" className="w-full h-full object-cover" data-id="element-3074" />
              </motion.div>
            </div>

            {/* Form Column */}
            <motion.div initial={{
            opacity: 0,
            y: 20,
            rotateY: -5
          }} whileInView={{
            opacity: 1,
            y: 0,
            rotateY: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} style={{
            perspective: 1000
          }} className="bg-[#fbf9f6] dark:bg-[#454443] rounded-2xl p-8 md:p-12 border border-stone-300 dark:border-[#555453] shadow-xl" data-id="element-3075">
              <div className="text-center mb-10" data-id="element-3076">
                <h2 className="font-heading text-3xl font-bold text-stone-900 dark:text-white mb-4" data-id="element-3077">
                  Apply for a Pro Account
                </h2>
                <p className="text-stone-600 dark:text-stone-200" data-id="element-3078">
                  Fill out the form below and our team will be in touch shortly.
                </p>
              </div>

              {isSubmitted ? <div className="flex flex-col items-center justify-center text-center py-12" data-id="element-3079">
                  <div className="w-16 h-16 bg-[#fbbf24]/15 rounded-full flex items-center justify-center mb-6" data-id="element-3080">
                    <CheckCircle2 className="w-8 h-8 text-[#f59e0b] dark:text-[#fbbf24]" data-id="element-3081" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-stone-900 dark:text-white mb-3" data-id="element-3082">
                    Application Received!
                  </h3>
                  <p className="text-stone-600 dark:text-stone-200 mb-8 max-w-md" data-id="element-3083">
                    Thank you for applying to the 253Gravel Contractor Program.
                    We will review your details and contact you within 24 hours.
                  </p>
                  <button onClick={() => setIsSubmitted(false)} className="px-8 py-3 bg-[#fbbf24] hover:bg-[#f59e0b] text-stone-900 rounded-none font-bold transition-colors" data-id="element-3084">
                    Submit Another Application
                  </button>
                </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-3085">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3086">
                    <div data-id="element-3087">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-3088">
                        Company Name *
                      </label>
                      <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} required className={inputClass} data-id="element-3089" />
                    </div>
                    <div data-id="element-3090">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-3091">
                        Contact Name *
                      </label>
                      <input type="text" name="contactName" value={formData.contactName} onChange={handleChange} required className={inputClass} data-id="element-3092" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3093">
                    <div data-id="element-3094">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-3095">
                        Email Address *
                      </label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} data-id="element-3096" />
                    </div>
                    <div data-id="element-3097">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-3098">
                        Phone Number *
                      </label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className={inputClass} data-id="element-3099" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3100">
                    <div data-id="element-3101">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-3102">
                        Contractor License # (Optional)
                      </label>
                      <input type="text" name="licenseNumber" value={formData.licenseNumber} onChange={handleChange} className={inputClass} data-id="element-3103" />
                    </div>
                    <div data-id="element-3104">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-3105">
                        Estimated Monthly Volume *
                      </label>
                      <select name="estimatedVolume" value={formData.estimatedVolume} onChange={handleChange} required className={inputClass + ' appearance-none'} data-id="element-3106">
                        <option value="" disabled data-id="element-3107">
                          Select volume...
                        </option>
                        <option value="1-5" data-id="element-3108">1-5 Deliveries</option>
                        <option value="6-15" data-id="element-3109">6-15 Deliveries</option>
                        <option value="16-30" data-id="element-3110">16-30 Deliveries</option>
                        <option value="31+" data-id="element-3111">31+ Deliveries</option>
                      </select>
                    </div>
                  </div>

                  <div className="pt-4" data-id="element-3112">
                    <button type="submit" disabled={isSubmitting} className="relative w-full inline-flex items-center justify-center px-8 py-4 bg-[#fbbf24] hover:bg-[#f59e0b] text-stone-900 rounded-none font-bold text-lg transition-all group disabled:opacity-70 overflow-hidden" data-id="element-3113">
                      {/* Subtle idle shimmer */}
                      {!isSubmitting && <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12" animate={{
                    x: ['-200%', '200%']
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 4,
                    ease: 'easeInOut'
                  }} data-id="element-3114" />}

                      {isSubmitting ? <div className="w-6 h-6 border-2 border-stone-900 border-t-transparent rounded-full animate-spin relative z-10" data-id="element-3115" /> : <span className="relative z-10 flex items-center" data-id="element-3116">
                          Submit Application
                          <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-3117" />
                        </span>}
                    </button>
                  </div>
                </form>}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Contact CTA */}
      <section className="py-16 bg-white dark:bg-[#2a2a2a] relative z-10" data-id="element-3118">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3119">
          <motion.div animate={{
          y: [0, -3, 0]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="bg-black dark:bg-gradient-to-br dark:from-[#4a4a4a] dark:to-[#3a3a3a] rounded-2xl p-8 md:p-12 border border-black dark:border-[#5a5a5a] text-center shadow-2xl" data-id="element-3120">
            <h2 className="font-heading text-3xl font-bold text-white mb-4" data-id="element-3121">
              Need a Custom Quote?
            </h2>
            <p className="text-white/70 dark:text-stone-200 mb-8 max-w-2xl mx-auto" data-id="element-3122">
              For orders over 100 yards, complex deliveries, or to set up a
              net-30 account, contact our dispatch team directly.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-3123">
              <a href="tel:206-619-3742" className="flex items-center justify-center px-8 py-4 bg-[#fbbf24] hover:bg-[#f59e0b] text-stone-900 rounded-none font-bold text-lg transition-colors w-full sm:w-auto" data-id="element-3124">
                <div className="relative flex items-center justify-center w-5 h-5 mr-2" data-id="element-3125">
                  <motion.div className="absolute inset-0 rounded-full border border-stone-900" animate={{
                  scale: [1, 1.4],
                  opacity: [0.8, 0]
                }} transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: 'easeOut'
                }} data-id="element-3126" />
                  <Phone className="w-4 h-4 relative z-10" data-id="element-3127" />
                </div>
                (206) 619-3742
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Link to Materials Hub */}
      <section className="py-20 bg-stone-50 dark:bg-[#252525] relative z-10" data-id="element-3128">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3129">
          <Link to="/#materials" className="inline-flex items-center gap-3 font-heading text-3xl md:text-4xl font-extrabold text-[#f59e0b] dark:text-[#fbbf24] hover:text-[#d97706] dark:hover:text-[#f59e0b] transition-colors group" data-id="element-3130">
            Browse Our Materials Hub
            <motion.div animate={{
            x: [0, 4, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-3131">
              <ArrowRight className="w-8 h-8 group-hover:translate-x-2 transition-transform" data-id="element-3132" />
            </motion.div>
          </Link>
          <p className="text-stone-600 dark:text-stone-300 mt-4 text-lg" data-id="element-3133">
            View our full product catalog with pricing and ordering.
          </p>
        </div>
      </section>
    </main>;
}